import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  CreateDelovodniDokumentCommand,
  DeleteDelovodniDokumentFileCommand,
  DelovodniciClient,
  DelovodnikInfoDto,
  FileParameter,
  GetDelovodnikPresignedUrlQuery,
  HttpVerb,
  UpdateDelovodniDokumentCommand,
} from '@kodit/core/data-api';
import { FormHelper } from '@kodit/core/form-definitions';
import { AlertService, SharedService } from '@kodit/core/services';
import { DynamicDialogConfig, DynamicDialogRef, FileUploadService } from '@kodit/core/shared-ui';
import {
  DelovodniDokumentForm,
  DelovodnikService
} from 'libs/core/moduli/podesavanje/src/lib/delovodnik/delovodnik-service';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'kodit-delovodni-dokument-form',
  templateUrl: './delovodni-dokument-form.component.html',
  styleUrls: ['./delovodni-dokument-form.component.scss'],
})
export class DelovodniDokumentFormComponent implements OnInit, OnDestroy {
  /** Subs */
  private _subs: Subscription = new Subscription();
  /** Forms */
  form!: DelovodniDokumentForm;

  /** Props */
  delovodnikDto: DelovodnikInfoDto | undefined;
  file: FileParameter | undefined;
  isEdit: boolean = false;
  selectedKategorijaNaziv: string | undefined;
  attachmentUrl?: string;
  hasExistingFile = false;

  constructor(
    private _dialogConfig: DynamicDialogConfig,
    private _delovodnikService: DelovodnikService,
    private _client: DelovodniciClient,
    private _dialogRef: DynamicDialogRef,
    private _formHelper: FormHelper,
    private _alertService: AlertService,
    private _sharedService: SharedService,
    private _fileService: FileUploadService
  ) {}

  onUpload(file: File) {
    this.file = { fileName: file.name, data: file };
  }

  ngOnInit(): void {
    this.delovodnikDto = this._dialogConfig.data.delovodnikDto;

    this._initForm(this._dialogConfig.data.id);

    this._subs.add(
      this._dialogRef.onSubmit.subscribe(() => {
        this.save();
      })
    );
  }

  private save() {
    if (!this.form.valid) {
      this._formHelper.invalidateForm(this.form);
      return;
    }

    this._sharedService.displayLoadingScreen(
      true,
      this.isEdit ? 'Ažuriranje delovodnog dokumenta...' : 'Kreiranje delovodnog dokumenta...'
    );

    // ako nema fajla
    if (!this.file) {
      let request$ = this.isEdit
        ? this._client.updateDelovodniDokument(this.form.value as UpdateDelovodniDokumentCommand)
        : this._client.createDelovodniDokument(this.form.value as CreateDelovodniDokumentCommand);

      this._subs.add(
        request$
          .pipe(
            finalize(() => {
              this._sharedService.resetLoadingScreen();
              this._dialogRef.close(true);
            })
          )
          .subscribe({
            next: () => this.showSuccessMsg()
          })
      );

      return;
    }

    // ako postoji fajl
    this._subs.add(
      this._client
        .getPresignedUrl(
          new GetDelovodnikPresignedUrlQuery({
            delovodnikId: this.delovodnikDto?.id,
            action: HttpVerb.PUT,
            contentType: this.file.data.type,
            fileName: this.file.data.name,
          })
        )
        .subscribe((getUrlRes) => {
          this._subs.add(
            this._fileService
              .uploadFileToS3(
                getUrlRes.data!.url!,
                this.file!.data,
                getUrlRes.data!.contentType!
              )
              .subscribe(() => {
                this.form.patchValue({
                  storageFileName: getUrlRes.data!.storageFileName,
                  originalFileName: getUrlRes.data!.originalFileName,
                });

                let request$ = this.isEdit
                  ? this._client.updateDelovodniDokument(this.form.value as UpdateDelovodniDokumentCommand)
                  : this._client.createDelovodniDokument(this.form.value as CreateDelovodniDokumentCommand);

                this._subs.add(
                  request$
                    .pipe(
                      finalize(() => {
                        this._sharedService.resetLoadingScreen();
                        this._dialogRef.close(true);
                      })
                    )
                    .subscribe({
                      next: () => this.showSuccessMsg()
                    })
                );
              })
          );
        })
    );
  }

  private _initForm(delovodniDokumentId?: number) {
    if (delovodniDokumentId) {
      this.isEdit = true;

      this._subs.add(
        this._client.getDelovodniDokument(delovodniDokumentId)
          .subscribe(result => {

            // popunimo formu sa backendaa
            this.form = this._delovodnikService.getDelovodniDokumentForm(
              this.delovodnikDto?.id!,
              this.delovodnikDto?.kategorijaId,
              result.data
            );

            // naziv kategorije samog dokumenta (za prikaz u dropdownu na editu)
            this.selectedKategorijaNaziv = result.data?.kategorijaNaziv;

            this.hasExistingFile = !!result.data!.storageFileName;  // da li postoji fajl

            // ako postoji fajl onda pribavimo presignedurl
            if (this.hasExistingFile) {

              this._subs.add(
                this._client.getPresignedUrl(
                  new GetDelovodnikPresignedUrlQuery({
                    delovodnikId: this.delovodnikDto?.id,
                    action: HttpVerb.GET,
                    fileName: result.data!.storageFileName
                  })
                )
                .subscribe(res => {
                  this.attachmentUrl = res.data!.url;
                })
              );

            }

          })
      );

      return;
    }

    this.form = this._delovodnikService.getDelovodniDokumentForm(
      this.delovodnikDto?.id!,
      this.delovodnikDto?.kategorijaId
    );
  }

  removeExistingFile() {
    const dokumentId = this.form.value.delovodniDokumentId;
    if (!dokumentId) return;

    this._subs.add(
      this._client.deleteDelovodniDokumentFile(new DeleteDelovodniDokumentFileCommand({ delovodniDokumentId: dokumentId }))
      .subscribe({
        next: () => {
          this.form.patchValue({
            storageFileName: undefined,
            originalFileName: undefined
          });
          this.hasExistingFile = false;
          this.attachmentUrl = undefined;
          this._alertService.addSuccessMsg('Prilog uspešno obrisan');
        }
        })
    );
  }

  private showSuccessMsg() {
    if (this.isEdit) {
      this._alertService.addSuccessMsg('Uspešno izmenjen delovodni dokument');
    } else {
      this._alertService.addSuccessMsg('Uspešno kreiran delovodni dokument');
    }
  }

  ngOnDestroy() {
    this._subs.unsubscribe();
  }
}
